import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { PromotionsContext } from '../contexts/promotionsContext';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import {
  appendCategoryDetails,
  checkAndUpdateFreeCart,
  checkPromoCodeAppliable,
  combineCarts,
  duplicateCartItems,
  filterFreePromotions,
  filterOutDeliveryPromotions,
  filterOutUnAppliableOrderPromotions,
  findAppliedPromotions,
  getPromoCodeVerificationPayload,
  promoCodeHasRedeemLimit,
  removeUnavailablePromotionId,
  sortCartByLastUpdatedAt,
  sortPromotions,
  splitPromoCodes,
} from '../helpers/utils';
import { calculateAmountsAndBreakdowns } from '../../../helpers/itemCalculations';
import { promotionsOperations } from '../../../state/features/promotions';
import { verificationPayloadCreator } from '../helpers/verificationPayload';
import ModifierPopup from '../helpers/modifierPopUp';
import {
  PROMO_CODE_VERIFICATION_STATUS,
  promoCodeType,
} from '../types/promotionTypes';
import { loaderOperations } from '../../../state/features/loader';

interface IPromotionsProviderProps {
  children: ReactNode;
  verifyPromoCode(payload: any): any;
  removePromotionReservation(payload: any): any;
}

const PromotionsProvider = ({
  children,
  verifyPromoCode,
  removePromotionReservation,
}: IPromotionsProviderProps) => {
  const [userAcknowledgedPromotions, setUserAcknowledgedPromotions] = useState<
    any[]
  >([]);
  const [userClosePromotionPopup, setUserClosePromotionPopup] =
    useState<boolean>(false);
  const [freeItemCart, setFreeItemCart] = useState<any[]>([]);
  const [appliedPromoCode, setAppliedPromoCode] = useState<string>('');
  const [promoVerificationStatus, setPromoVerificationStatus] =
    useState<string>('');
  const [reservationKey, setReservationKey] = useState<string>('');

  const { availablePromotions } = useSelector(
    (state: RootState) => state.promotions
  );
  const { items: userCartItems } = useSelector(
    (state: RootState) => state.cart
  );
  const { order_type: orderType } = useSelector(
    (state: RootState) => state.session
  );
  const { company, user, outlet, menu, discount, riderTip, session } = useSelector(
    (state: RootState) => state
  );
  const { redeemedPoints, customer_loyalty } = useSelector((state: RootState) => state.loyalty);
  const { selectedArea } = useSelector((state: RootState) => state.areas);
  const { delivery_charge_id } = useSelector(
    (state: RootState) => state.outlet
  );
  const dispatch = useDispatch();

  const transformedMenu = useMemo(() => {
    const categories = menu.categories || [];
    if (!categories.length) return [];

    return categories.flatMap((category: any) =>
      category.items.flatMap((item: any) =>
        item.items && item.items.length ? item.items : [item]
      )
    );
  }, [JSON.stringify(menu.categories)]);

  useEffect(() => {
    const storedAppliedPromoCode =
      JSON.parse(sessionStorage.getItem('applied_promo_code') || '[]') || '';
    const storedPromoPayload = sessionStorage.getItem('promo_payload');
    const promoPayload = storedPromoPayload
      ? JSON.parse(storedPromoPayload)
      : null;
    if (
      appliedPromoCode === '' &&
      storedAppliedPromoCode !== '' &&
      promoPayload !== null
    ) {
      clearPromoCodeReservation(promoPayload);
      sessionStorage.removeItem('promo_payload');
      sessionStorage.removeItem('applied_promo_code');
    }
    const storedItems = localStorage.getItem('free_items_cart');
    let localStoredFreeItems = storedItems ? JSON.parse(storedItems) : [];
    if (localStoredFreeItems.length && localStoredFreeItems !== freeItemCart) {
      updateFreeItemCart(localStoredFreeItems);
    }
  }, []);

  useEffect(() => {
    let cartItems =
      userCartItems.length > 0
        ? userCartItems
        : localStorage.getItem('cartItems')
        ? JSON.parse(localStorage.getItem('cartItems') || '[]')
        : [];
    if (cartItems.length === 0 && appliedPromoCode) {
      resetPromocode();
    }
    if (cartItems.length === 0) {
      //reset the promo code when the cart is empty after order completion
      const localStorageCartItems = JSON.parse(
        localStorage.getItem('cartItems') || '[]',
      );
      if (localStorageCartItems.length === 0) {
        setAppliedPromoCode('');
        setPromoVerificationStatus('');
      }
    }
  }, [JSON.stringify(userCartItems)]);

  useEffect(() => {
    if (
      user &&
      user.sapaad_customer_id &&
      appliedPromoCode &&
      appliedPromoCode !== ''
    ) {
      const couponData = getPromoCodeVerificationPayload(
        appliedPromoCode,
        availablePromotions,
        company.sapaad_company_id,
        user.sapaad_customer_id,
        true
      );
      if (couponData) {
        let promoCodeDetails = {
          ...couponData,
          location_id: outlet.sapaad_location_id,
          reservation_key: reservationKey,
        };
        dispatch(
          promotionsOperations.updateAppliedPromoCodeDetails(promoCodeDetails)
        );
      }
    }
  }, [
    appliedPromoCode,
    reservationKey,
    availablePromotions,
    outlet.sapaad_location_id,
    company.sapaad_company_id,
    user.sapaad_customer_id,
  ]);

  const clearPromoCodeReservation = async (
    promoCodeVerificationPayload: any
  ) => {
    try {
      dispatch(loaderOperations.showLoader());
      const result = await removePromotionReservation(
        promoCodeVerificationPayload
      );
      if (
        result.payload?.data?.message === 'Reservation removed successfully.'
      ) {
        setAppliedPromoCode('');
        setPromoVerificationStatus('');
        sessionStorage.removeItem('applied_promo_code');
      }
    } catch (error) {
      console.error('An unexpected error occurred:', error);
    } finally {
      dispatch(loaderOperations.hideLoader());
    }
  };

  const resetPromocode = () => {
    // Get all promotions with coupon codes (not filtered by applicability)
    // This ensures we can find the promotion even when cart is empty or conditions change
    const allPromotionsWithCodes = availablePromotions.filter(
      (promotion: any) =>
        promotion.coupon_codes && promotion.coupon_codes.length > 0
    );

    const hasRedeemLimit = promoCodeHasRedeemLimit(
      appliedPromoCode,
      allPromotionsWithCodes
    );
    if (hasRedeemLimit) {
      //if the promo code has a redeem limit, clear the reservation
      const promoVerificationPayload = getPromoCodeVerificationPayload(
        appliedPromoCode,
        allPromotionsWithCodes,
        company.sapaad_company_id,
        user.sapaad_customer_id,
        true // Skip applicability check since we're clearing the reservation
      );
      if (!promoVerificationPayload) {
        console.error('No valid promotion found for the given promo code.');
        return;
      }
      clearPromoCodeReservation(promoVerificationPayload);
    } else {
      //if the promo code does not have a redeem limit, reset the promo code
      sessionStorage.removeItem('applied_promo_code');
      setAppliedPromoCode('');
      setPromoVerificationStatus('');
    }
  };

  const cartVerificationPayload = useMemo(() => {
    const verificationPayload = verificationPayloadCreator({
      userCartItems: userCartItems,
      menu: menu,
      orderType: orderType,
      discount: discount,
      redeemedPoints: redeemedPoints,
      selectedArea: selectedArea,
      deliveryChargeId: delivery_charge_id,
      riderTip: riderTip,
      orderPromotions: [], //promotion not required
      company: company,
      outlet: outlet,
      user: user,
      customer_loyalty: customer_loyalty,
    });
    return verificationPayload;
  }, [
    JSON.stringify(userCartItems),
    menu,
    orderType,
    discount,
    redeemedPoints,
    JSON.stringify(selectedArea),
    selectedArea?.id,
    selectedArea?.name,
    selectedArea?.delivery_charge,
    delivery_charge_id,
    riderTip,
    company,
    outlet.id,
    outlet.delivery_charge_id,
    user,
    customer_loyalty,
    session,
  ]);

  const transformedCart = useMemo(() => {
    if (!userCartItems.length) return [];

    return duplicateCartItems(
      removeUnavailablePromotionId(
        userCartItems
          .map(
            (item: any) => appendCategoryDetails(item, menu.categories) //appending category information to cart items
          )
          .filter((item: any) => item != null), // Filter out null/undefined items
        availablePromotions
      )
    );
  }, [JSON.stringify(userCartItems), menu.categories, availablePromotions]);

  const freeItemsDependency = useMemo(() => {
    if (!freeItemCart) return '';
    return JSON.stringify(
      freeItemCart?.map((item) => ({
        id: item.id,
        groups: item.groups?.map((group: any) => ({
          id: group.id,
          items: group.items?.map((item: any) => ({
            id: item.id,
            count: item.count || 0,
            selected: item.selected || false,
          })),
        })),
      }))
    );
  }, [
    freeItemCart
      ?.map((item) =>
        item.groups
          ?.map((group: any) =>
            group.items
              ?.map((item: any) => `${item.id}-${item.count}-${item.selected}`)
              .join(',')
          )
          .join('|')
      )
      .join('||'),
  ]);

  // Second memo to store the actual data
  // prevent re-rendering of the free items cart and circular dependency
  const freeItemsCartCopy = useMemo(() => {
    if (!freeItemCart) return [];
    return freeItemCart;
  }, [freeItemsDependency]);

  const appliedPromotionsData = useMemo(() => {
    let appliedPromotions: any[] = [];
    let promotionAppliedCart: any[] = [];
    let orderPromotion: any[] = [];
    let appliablePromotions: any[] = availablePromotions;
    let updatedFreeItems: any[] = [];
    let availablePromoCodes: any[] = [];
    let freeItemsCartMemo: any[] = freeItemsCartCopy;
    if (availablePromotions.length) {
      const cartSubTotal = userCartItems.reduce(
        (acc: number, item: any) => acc + parseFloat(item.sub_total),
        0
      );
      if (orderType !== 'delivery') {
        //filter out delivery promotions
        appliablePromotions = filterOutDeliveryPromotions(appliablePromotions);
      }
      //filter free promotions which have out of stock items or items which are not in the menu
      appliablePromotions = filterFreePromotions(
        appliablePromotions,
        menu.out_of_stock_items,
        transformedMenu
      );
      //split promotions into promotions with promo codes and promotions without promo codes
      let splitPromoData = splitPromoCodes(
        appliablePromotions,
        appliedPromoCode
      );
      availablePromoCodes = splitPromoData.promotionWithPromoCodes;
      if (availablePromoCodes.length) {
        availablePromoCodes = checkPromoCodeAppliable(
          availablePromoCodes,
          transformedCart,
          transformedMenu,
          updatedFreeItems,
          freeItemsCartMemo
        );
      }
      appliablePromotions = splitPromoData.promotionWithoutPromoCodes;
      appliablePromotions = filterOutUnAppliableOrderPromotions(
        appliablePromotions,
        cartSubTotal
      );
      if (appliedPromoCode && appliedPromoCode !== '') {
        //filter out promotions which do not have the applied promo code
        appliablePromotions = appliablePromotions.filter((promotion) => {
          return promotion?.coupon_codes?.some(
            (coupon: any) => coupon.code === appliedPromoCode
          );
        });
      }
      //find applied promotions
      if (transformedCart.length && appliablePromotions.length) {
        let sortedPromotions = sortPromotions(appliablePromotions);
        let promotionData = findAppliedPromotions(
          transformedCart,
          sortedPromotions,
          transformedMenu,
          updatedFreeItems,
          freeItemsCartMemo
        );
        appliedPromotions = promotionData.appliedPromotions;
        promotionAppliedCart = promotionData.promotionAppliedCart;
        promotionAppliedCart = sortCartByLastUpdatedAt(promotionAppliedCart);
        updatedFreeItems = checkAndUpdateFreeCart(
          freeItemCart,
          promotionData.freeItemCart
        );
        orderPromotion = sortPromotions(promotionData.orderPromotion);
        if (orderPromotion.length) {
          appliedPromotions.push(orderPromotion[0]);
        }
      } else {
        if (transformedCart.length === 0) {
          updatedFreeItems = freeItemCart;
          const cartItemsFromLocalStorage = JSON.parse(
            localStorage.getItem('cartItems') || '[]'
          );
          //when cart is actually empty, free items cart and promotion applied cart are also cleared
          if (cartItemsFromLocalStorage.length === 0) {
            localStorage.setItem('free_items_cart', JSON.stringify([]));
            localStorage.setItem('promotion_applied_cart', JSON.stringify([]));
            updatedFreeItems = [];
          }
        }
        promotionAppliedCart = userCartItems ?? [];
      }
    } else {
      promotionAppliedCart = userCartItems ?? [];
      updatedFreeItems = freeItemCart ?? [];
      availablePromoCodes = [];
      appliedPromotions = [];
      orderPromotion = [];
    }
    return {
      appliedPromotions,
      promotionAppliedCart,
      orderPromotion,
      updatedFreeItems,
      availablePromoCodes,
    };
  }, [
    transformedCart,
    availablePromotions,
    orderType,
    transformedMenu,
    appliedPromoCode,
    menu.out_of_stock_items,
    JSON.stringify(freeItemsCartCopy || []),
  ]);

  /**
   * Calculates the free items without updated groups
   * A free item with groups that have all modifiers count as 0 is not pending for modifier update
   */
  const pendingModifierUpdateFreeItems = useMemo(() => {
    const updatedFreeCart: any[] = freeItemCart ?? [];
    let freeItemsWithoutUpdatedGroups: any[] = [];
    if (updatedFreeCart.length) {
      freeItemsWithoutUpdatedGroups = updatedFreeCart.filter(
        (item) =>
          item.groups &&
          item.groups.length &&
          item.groups.every(
            (group: any) =>
              group.items &&
              group.items.length &&
              group.items.every(
                (modifier: any) => !modifier.count || modifier.count === 0
              )
          )
      );
    }
    return freeItemsWithoutUpdatedGroups;
  }, [JSON.stringify(freeItemCart)]);

  const newlyAppliedPromotions = useMemo(
    () =>
      appliedPromotionsData.appliedPromotions.filter(
        (promotion) =>
          !userAcknowledgedPromotions.some((ack) => ack.id === promotion.id)
      ),
    [appliedPromotionsData.appliedPromotions, userAcknowledgedPromotions]
  );

  const promotionCartBreakdowns = useMemo(() => {
    const userCart = combineCarts(
      appliedPromotionsData.promotionAppliedCart,
      freeItemCart
    );
    let amountBreakdowns: any = calculateAmountsAndBreakdowns(
      userCart,
      menu,
      orderType,
      discount || null,
      redeemedPoints || null,
      selectedArea || {},
      delivery_charge_id || null,
      riderTip || 0,
      appliedPromotionsData.orderPromotion
    );
    return amountBreakdowns;
  }, [
    appliedPromotionsData,
    menu,
    orderType,
    discount,
    redeemedPoints,
    selectedArea,
    selectedArea?.id,
    selectedArea?.name,
    selectedArea?.delivery_charge,
    delivery_charge_id,
    riderTip,
  ]);

  const showModifierPopup = useMemo(
    () => pendingModifierUpdateFreeItems.length > 0 && userClosePromotionPopup,
    [pendingModifierUpdateFreeItems, userClosePromotionPopup]
  );

  const availablePromoCodes = useMemo(() => {
    let promoCodes: promoCodeType[] = [];
    if (appliedPromotionsData.availablePromoCodes.length) {
      const promosWithCodes = appliedPromotionsData.availablePromoCodes.filter(
        (promo: any) => promo.coupon_codes && promo.coupon_codes.length
      );
      promoCodes = promosWithCodes.reduce((acc: any[], promo: any) => {
        const promoTriggeringRule = promo.triggering_rule || {};
        const promoCodeItems = promo.coupon_codes.map((couponCode: any) => ({
          ...couponCode,
          name: promo.name || '',
          description: promo.description || '',
          condition_fullfilled: couponCode.condition_fullfilled || false,
          has_triggering_rule:
            Object.keys(promoTriggeringRule).length > 0 ? true : false,
        }));
        return [...acc, ...promoCodeItems];
      }, []);
    }
    return promoCodes;
  }, [appliedPromotionsData.availablePromoCodes]);

  const promoCodesWithTriggeringRule = useMemo(() => {
    return availablePromoCodes.filter(
      (promo: any) => promo.has_triggering_rule
    );
  }, [availablePromoCodes]);

  const promoCodeWidgetData = useMemo(() => {
    return {
      appliedPromoCode,
      promoVerificationStatus,
    };
  }, [appliedPromoCode, promoVerificationStatus]);

  /**
   * Automatically remove coupon codes when their conditions are no longer met
   * For example, if a coupon requires minimum $100 and cart drops below that amount
   */
  useEffect(() => {
    // Only check if there's an active promo code
    if (!appliedPromoCode || appliedPromoCode === '') {
      return;
    }

    // Check if the promo code's conditions are still fulfilled
    const availablePromoCodes = appliedPromotionsData.availablePromoCodes;
    if (availablePromoCodes.length > 0) {
      // Find the currently applied promo code
      const currentPromotion = availablePromoCodes.find((promo: any) =>
        promo.coupon_codes?.some(
          (coupon: any) => coupon.code === appliedPromoCode
        )
      );

      if (currentPromotion) {
        // Check if all coupon codes have condition_fullfilled set to false
        const allConditionsNotMet = currentPromotion.coupon_codes.every(
          (coupon: any) => coupon.condition_fullfilled === false
        );

        // If conditions are no longer met, automatically reset the promo code
        if (allConditionsNotMet) {
          resetPromocode();
        }
      }
    }
  }, [appliedPromoCode, appliedPromotionsData.availablePromoCodes]);

  const userAcknowledgedPromotionsHandler = (newpromotions: any[]) => {
    setUserAcknowledgedPromotions((prev) => [...prev, ...newpromotions]); //adding newly acknowledged promotions to existing list
  };

  useEffect(() => {
    if (availablePromotions.length) {
      const removedPromotions = userAcknowledgedPromotions.filter(
        (ack) =>
          !appliedPromotionsData.appliedPromotions.some(
            (promo) => promo.id === ack.id
          )
      );

      if (removedPromotions.length > 0) {
        setUserAcknowledgedPromotions((prev) =>
          prev.filter(
            (ack) => !removedPromotions.some((removed) => removed.id === ack.id)
          )
        );
      }
    }
  }, [appliedPromotionsData.appliedPromotions, userAcknowledgedPromotions]);

  useEffect(() => {
    if (
      JSON.stringify(appliedPromotionsData.updatedFreeItems) !==
      JSON.stringify(freeItemCart)
    ) {
      updateFreeItemCart(appliedPromotionsData.updatedFreeItems);
    }
  }, [appliedPromotionsData.updatedFreeItems]);

  useEffect(() => {
    if (userClosePromotionPopup) {
      setUserClosePromotionPopup(false);
    }
  }, [JSON.stringify(userCartItems)]);

  useEffect(() => {
    dispatch(
      promotionsOperations.updatedAppliedPromotion(
        appliedPromotionsData.appliedPromotions
      )
    );
  }, [JSON.stringify(appliedPromotionsData.appliedPromotions)]);

  // Memoize the cart to prevent unnecessary re-renders
  const memoizedCart = useMemo(
    () => appliedPromotionsData.promotionAppliedCart,
    [JSON.stringify(appliedPromotionsData.promotionAppliedCart)]
  );

  useEffect(() => {
    if (memoizedCart.length) {
      localStorage.setItem(
        'promotion_applied_cart',
        JSON.stringify(memoizedCart)
      );
    }
    dispatch(promotionsOperations.updatePromotionAppliedCart(memoizedCart));
  }, [memoizedCart]);

  useEffect(() => {
    dispatch(
      promotionsOperations.updateAppliedOrderPromotions(
        appliedPromotionsData.orderPromotion
      )
    );
  }, [JSON.stringify(appliedPromotionsData.orderPromotion)]);

  useEffect(() => {
    dispatch(promotionsOperations.updateFreeItemsCart(freeItemCart));
  }, [JSON.stringify(freeItemCart)]);

  const updateFreeItemCart = (newFreeItemsCart: any) => {
    localStorage.setItem(
      'free_items_cart',
      JSON.stringify(newFreeItemsCart || [])
    );
    setFreeItemCart(newFreeItemsCart);
  };

  const toggleUserClosePromotionPopup = (updatedState: boolean) => {
    setUserClosePromotionPopup(updatedState);
  };

  const onPromoCodeApply = async (promoCode: string) => {
    setPromoVerificationStatus(PROMO_CODE_VERIFICATION_STATUS.LOADING);

    const promoVerificationPayload = getPromoCodeVerificationPayload(
      promoCode,
      appliedPromotionsData.availablePromoCodes,
      company.sapaad_company_id,
      user.sapaad_customer_id
    );

    if (!promoVerificationPayload) {
      setPromoVerificationStatus(PROMO_CODE_VERIFICATION_STATUS.INVALID);
      setAppliedPromoCode('');
      return;
    } else {
      const currentPromoHasRedeemLimit = promoCodeHasRedeemLimit(
        promoCode,
        appliedPromotionsData.availablePromoCodes
      );
      if (currentPromoHasRedeemLimit) {
        sessionStorage.setItem(
          'promo_payload',
          JSON.stringify(promoVerificationPayload)
        );
      }
    }

    try {
      dispatch(loaderOperations.showLoader());
      const result = await verifyPromoCode(promoVerificationPayload);
      if (result.payload?.data?.is_valid) {
        setPromoVerificationStatus(PROMO_CODE_VERIFICATION_STATUS.SUCCESS);
        setAppliedPromoCode(promoCode);
        setReservationKey(result.payload?.data?.reservation_key);
        sessionStorage.setItem('applied_promo_code', JSON.stringify(promoCode));
      } else {
        setPromoVerificationStatus(PROMO_CODE_VERIFICATION_STATUS.INVALID);
        setAppliedPromoCode('');
      }
    } catch (error) {
      console.error('An unexpected error occurred:', error);
      setAppliedPromoCode('');
      setPromoVerificationStatus(PROMO_CODE_VERIFICATION_STATUS.ERROR);
    } finally {
      dispatch(loaderOperations.hideLoader());
    }
  };

  return (
    <PromotionsContext.Provider
      value={{
        appliedPromotionsData,
        newlyAppliedPromotions,
        cartVerificationPayload,
        userAcknowledgedPromotionsHandler,
        promotionCartBreakdowns,
        toggleUserClosePromotionPopup,
        freeItemCart,
        onPromoCodeApply,
        availablePromoCodes,
        promoCodeWidgetData,
        resetPromocode,
        appliedPromoCode,
        promoCodesWithTriggeringRule,
      }}
    >
      {showModifierPopup ? (
        <ModifierPopup
          modifierItem={pendingModifierUpdateFreeItems[0]}
          updateFreeItemCart={updateFreeItemCart}
          freeItemCart={freeItemCart}
          toggleUserClosePromotionPopup={toggleUserClosePromotionPopup}
          availablePromotions={availablePromotions}
        />
      ) : null}
      {children}
    </PromotionsContext.Provider>
  );
};

const mapDispatchToProps = {
  verifyPromoCode: promotionsOperations.validatePromoCode,
  removePromotionReservation: promotionsOperations.removePromoReservation,
};

export default connect(null, mapDispatchToProps)(PromotionsProvider);
